'use client';

import { useState, useEffect } from 'react';
import AppImage from '@/components/ui/AppImage';
import Icon from '@/components/ui/AppIcon';

interface TeamMember {
  id: number;
  name: string;
  role: string;
  expertise: string;
  bio: string;
  image: string;
  alt: string;
  linkedin?: string;
}

interface LeadershipTeamProps {
  className?: string;
}

const LeadershipTeam = ({ className = '' }: LeadershipTeamProps) => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [selectedMember, setSelectedMember] = useState<TeamMember | null>(null);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const teamMembers: TeamMember[] = [
  {
    id: 1,
    name: 'Dr. Sarah Mitchell',
    role: 'Chief Executive Officer',
    expertise: 'Aerospace Systems Engineering',
    bio: '20+ years leading aerospace manufacturing operations for NASA and SpaceX programs. Ph.D. in Aerospace Engineering from MIT with specialization in advanced manufacturing systems.',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1e95358c6-1772081363912.png",
    alt: 'Professional woman with short brown hair in navy blazer smiling confidently in modern office',
    linkedin: '#'
  },
  {
    id: 2,
    name: 'James Chen',
    role: 'Chief Technology Officer',
    expertise: 'Advanced Manufacturing & Automation',
    bio: 'Former Boeing senior engineer with expertise in CNC machining, additive manufacturing, and Industry 4.0 integration. 15 years driving manufacturing innovation.',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_196d3ff14-1778788490114.png",
    alt: 'Asian man in glasses and dark suit with arms crossed in technology facility',
    linkedin: '#'
  },
  {
    id: 3,
    name: 'Colonel (Ret.) Marcus Thompson',
    role: 'VP of Defense Programs',
    expertise: 'Defense Manufacturing & ITAR Compliance',
    bio: '25 years military aerospace experience with deep expertise in defense manufacturing requirements, security protocols, and regulatory compliance.',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_19d993b6e-1772194683013.png",
    alt: 'Distinguished man with gray hair in formal business attire in executive office',
    linkedin: '#'
  },
  {
    id: 4,
    name: 'Dr. Priya Sharma',
    role: 'VP of Quality Assurance',
    expertise: 'AS9100 & Quality Systems',
    bio: 'Quality systems expert with 18 years ensuring aerospace manufacturing excellence. Led AS9100 certifications for multiple aerospace manufacturers.',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_19ed678c3-1772749024920.png",
    alt: 'Indian woman in white lab coat with safety glasses in quality testing laboratory',
    linkedin: '#'
  },
  {
    id: 5,
    name: 'Robert Anderson',
    role: 'VP of Operations',
    expertise: 'Manufacturing Operations & Supply Chain',
    bio: 'Operations leader with proven track record scaling aerospace manufacturing capacity. Expert in lean manufacturing and supply chain optimization.',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1a1a957e3-1772069995153.png",
    alt: 'Man with beard in blue shirt standing in manufacturing facility with equipment',
    linkedin: '#'
  },
  {
    id: 6,
    name: 'Elena Rodriguez',
    role: 'VP of Engineering Services',
    expertise: 'Technical Support & Customer Success',
    bio: 'Aerospace engineer dedicated to customer success with 12 years providing technical consultation and engineering support for complex projects.',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_131b9183d-1772624812813.png",
    alt: 'Hispanic woman in professional attire with tablet in aerospace engineering office',
    linkedin: '#'
  }];


  if (!isHydrated) {
    return (
      <section className={`py-20 lg:py-28 bg-card ${className}`}>
        <div className="mx-auto px-6 lg:px-8">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <div className="inline-flex items-center space-x-2 px-4 py-2 bg-muted rounded-full border border-accent/30 mb-6">
                <span className="font-cta text-xs text-accent tracking-wider">LEADERSHIP TEAM</span>
              </div>
              <h2 className="font-headline text-3xl lg:text-5xl font-bold text-foreground mb-6">
                Aerospace Manufacturing Experts
              </h2>
              <p className="font-body text-lg text-technical-gray max-w-3xl mx-auto">
                Our leadership team brings decades of aerospace industry experience, combining technical expertise with operational excellence to deliver mission-critical manufacturing solutions.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {teamMembers.map((member) =>
              <div key={member.id} className="bg-background border border-border rounded-xl overflow-hidden">
                  <div className="relative h-80 overflow-hidden">
                    <AppImage
                    src={member.image}
                    alt={member.alt}
                    className="w-full h-full object-cover" />

                  </div>
                  <div className="p-6">
                    <h3 className="font-headline text-xl font-bold text-foreground mb-1">
                      {member.name}
                    </h3>
                    <p className="font-cta text-sm text-accent mb-2">{member.role}</p>
                    <p className="font-body text-sm text-technical-gray mb-4">{member.expertise}</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>);

  }

  return (
    <section className={`py-20 lg:py-28 bg-card ${className}`}>
      <div className="mx-auto px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <div className="inline-flex items-center space-x-2 px-4 py-2 bg-muted rounded-full border border-accent/30 mb-6">
              <span className="font-cta text-xs text-accent tracking-wider">LEADERSHIP TEAM</span>
            </div>
            <h2 className="font-headline text-3xl lg:text-5xl font-bold text-foreground mb-6">
              Aerospace Manufacturing Experts
            </h2>
            <p className="font-body text-lg text-technical-gray max-w-3xl mx-auto">
              Our leadership team brings decades of aerospace industry experience, combining technical expertise with operational excellence to deliver mission-critical manufacturing solutions.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {teamMembers.map((member) =>
            <div
              key={member.id}
              className="group bg-background border border-border rounded-xl overflow-hidden hover:border-accent transition-all duration-300 hover:shadow-glow cursor-pointer"
              onClick={() => setSelectedMember(member)}>

                <div className="relative h-80 overflow-hidden">
                  <AppImage
                  src={member.image}
                  alt={member.alt}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />

                  <div className="absolute inset-0 bg-gradient-to-t from-primary/80 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                </div>
                <div className="p-6">
                  <h3 className="font-headline text-xl font-bold text-foreground mb-1 group-hover:text-accent transition-colors duration-300">
                    {member.name}
                  </h3>
                  <p className="font-cta text-sm text-accent mb-2">{member.role}</p>
                  <p className="font-body text-sm text-technical-gray mb-4">{member.expertise}</p>
                  <button className="flex items-center space-x-2 text-sm font-body text-accent hover:text-mission-orange transition-colors duration-300">
                    <span>View Profile</span>
                    <Icon name="ArrowRightIcon" size={16} />
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {selectedMember &&
      <div className="fixed inset-0 bg-primary/95 backdrop-blur-sm z-50 flex items-center justify-center p-6" onClick={() => setSelectedMember(null)}>
          <div className="bg-card border border-accent rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="relative h-64 overflow-hidden">
              <AppImage
              src={selectedMember.image}
              alt={selectedMember.alt}
              className="w-full h-full object-cover" />

              <button
              onClick={() => setSelectedMember(null)}
              className="absolute top-4 right-4 w-10 h-10 bg-primary/80 backdrop-blur-sm rounded-full flex items-center justify-center hover:bg-accent transition-colors duration-300">

                <Icon name="XMarkIcon" size={20} className="text-foreground" />
              </button>
            </div>
            <div className="p-8">
              <h3 className="font-headline text-2xl font-bold text-foreground mb-2">
                {selectedMember.name}
              </h3>
              <p className="font-cta text-accent mb-2">{selectedMember.role}</p>
              <p className="font-body text-sm text-technical-gray mb-6">{selectedMember.expertise}</p>
              <p className="font-body text-foreground leading-relaxed mb-6">
                {selectedMember.bio}
              </p>
              {selectedMember.linkedin &&
            <a
              href={selectedMember.linkedin}
              className="inline-flex items-center space-x-2 px-4 py-2 bg-accent text-primary font-body text-sm font-medium rounded-lg hover:bg-mission-orange transition-colors duration-300">

                  <span>Connect on LinkedIn</span>
                  <Icon name="ArrowTopRightOnSquareIcon" size={16} />
                </a>
            }
            </div>
          </div>
        </div>
      }
    </section>);

};

export default LeadershipTeam;